import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  useNavigate,
  useSearchParams,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addPdfDisclaimer } from '../utils/pdfDisclaimer';

import {
  Box,
  Paper,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  SwipeableDrawer,
  Divider,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';

import {
  ExpandMore,
  FilterList,
  Edit,
  Delete as DeleteIcon,
  Launch,
  Close,
  TrendingUp,
  TrendingDown,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Lightbulb as LightbulbIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  Healing as HealingIcon,
  TrendingFlat,
  Straighten as StraightenIcon,
  FolderOpen as FolderOpenIcon,
  ViewColumn as ViewColumnIcon,
  ViewStream as ViewStreamIcon
} from '@mui/icons-material';

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';

import { useSeguimientoTable } from '../SeguimientoTableContext';
import ListboxComponent from '../components/ListboxComponent ';
import AcbExperienceBadge from '../components/AcbExperienceBadge';
import TanteoBadge from '../components/TanteoBadge';

/** =========================================================
 *                UTILS
 * ========================================================= */ 
const getInitials = (nom, prenom) => {
  const firstInitial = nom ? nom[0] : '';
  const secondInitial = prenom ? prenom[0] : '';
  return (firstInitial + secondInitial).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return '#757575';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 50%)`;
};

const getContrastColor = (hexcolor) => {
  if (!hexcolor) return '#000000';
  const r = parseInt(hexcolor.slice(1, 3), 16);
  const g = parseInt(hexcolor.slice(3, 5), 16);
  const b = parseInt(hexcolor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

const joinWithDash = (...items) => items.filter(Boolean).join(' - ');

const getFormIcon = (forme) => {
  if (!forme) return null;
  switch (forme.toLowerCase()) {
    case 'high':
      return <TrendingUp sx={{ color: '#4caf50', fontSize: 20 }} />;
    case 'low':
      return <TrendingDown sx={{ color: '#f44336', fontSize: 20 }} />;
    case 'stable':
      return <TrendingFlat sx={{ color: '#ff9800', fontSize: 20 }} />;
    default:
      return null;
  }
};

/** =========================================================
 *  HOOK CUSTOM : détecte si l'action de navigation est "POP"
 * ========================================================= */
function useOnPop(callback) {
  const navigationType = useNavigationType(); // "PUSH", "POP", "REPLACE"
  useEffect(() => {
    if (navigationType === 'POP') {
      callback();
    }
  }, [navigationType, callback]);
}

/** =========================================================
 *                AVATAR UTILISATEUR
 * ========================================================= */
const UserAvatar = ({ user, mediaUrl, size = 24 }) => (
  <Tooltip
    arrow
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: 'white',
          '& .MuiTooltip-arrow': { color: 'white' },
          boxShadow: '0px 2px 8px rgba(0,0,0,0.15)'
        }
      }
    }}
    title={
      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          src={`${mediaUrl}/users/${user.id}.png`}
          sx={{ width: 40, height: 40 }}
        />
        <Box>
          <Typography sx={{ fontWeight: 'bold', color: 'black' }}>
            {user.prenom} {user.nom}
          </Typography>
          {user.username && (
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)' }}>
              {user.username}
            </Typography>
          )}
        </Box>
      </Box>
    }
  >
    <Avatar
      sx={{
        width: size,
        height: size,
        fontSize: `${size * 0.5}px`,
        bgcolor: getAvatarColor(user.nom + user.prenom)
      }}
    >
      {getInitials(user.prenom, user.nom)}
    </Avatar>
  </Tooltip>
);

/** =========================================================
 *                PLAYER CARD
 * ========================================================= */
const PlayerCard = ({
  player,
  onPlayerClick,
  onPlayerUrlClick,
  mediaUrl,
  disablePlayerLink
}) => {
  // Calculate decimal age if date_naissance exists
  const calculateDecimalAge = (dateNaissance, anneeNaissance) => {
    if (!dateNaissance || !anneeNaissance) return anneeNaissance;

    const now = new Date();
    const birthDate = new Date(dateNaissance);

    // Calculate the difference in milliseconds
    const ageInMilliseconds = now - birthDate;

    // Convert to years with decimal
    const ageInYears = ageInMilliseconds / (365.25 * 24 * 60 * 60 * 1000);

    // Return formatted string
    return `${anneeNaissance} (${ageInYears.toFixed(1)})`;
  };

  const ageDisplay = calculateDecimalAge(player.date_naissance, player.annee_naissance);

  const line2 = joinWithDash(
    player.poste,
    ageDisplay,
    player.taille ? `${player.taille}cm` : null
  );

  const passportPart = player.nationalite
    ? player.nationalite + (player.passport ? ` - (${player.passport})` : '')
    : '';

  // Build line3 with competitions_detail if available, otherwise fallback to old fields
  let line3Parts = [passportPart];

  if (player.competitions_detail && player.competitions_detail.length > 0) {
    // Group competitions by team
    const equipesMap = new Map();
    player.competitions_detail.forEach(comp => {
      if (!equipesMap.has(comp.equipe_id)) {
        equipesMap.set(comp.equipe_id, {
          equipe_nom: comp.equipe_nom,
          competitions: []
        });
      }
      equipesMap.get(comp.equipe_id).competitions.push(comp.competition_nom);
    });

    // Format as: Team1 (Comp1, Comp2) - Team2 (Comp3)
    const teamsStr = Array.from(equipesMap.values())
      .map(equipe => `${equipe.equipe_nom} (${equipe.competitions.join(', ')})`)
      .join(' - ');

    if (teamsStr) line3Parts.push(teamsStr);
  } else {
    // Fallback to old fields
    if (player.team) line3Parts.push(player.team);
    const leagues = [player.league_1, player.league_2, player.competicion_europa]
      .filter(Boolean)
      .join(' - ');
    if (leagues) line3Parts.push(leagues);
  }

  const line3 = line3Parts.filter(Boolean).join(' - ');

  return (
    <ListItem
      button
      onClick={!disablePlayerLink ? () => onPlayerClick(player) : undefined}
      sx={{
        mb: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: disablePlayerLink ? 'background.paper' : 'action.hover'
        },
        py: 2,
        cursor: disablePlayerLink ? 'default' : 'pointer'
      }}
    >
      <ListItemAvatar>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={`${mediaUrl}/photos/${player.id}.png`}
            alt={player.nom}
            sx={{ width: 64, height: 64 }}
          />
          {player.forme_indicator && (
            <Box
              sx={{
                position: 'absolute',
                bottom: -4,
                right: -4,
                backgroundColor: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              {getFormIcon(player.forme_indicator)}
            </Box>
          )}
        </Box>
      </ListItemAvatar>

      <Box sx={{ ml: 1, flexGrow: 1 }}>
        <Typography
          variant="subtitle1"
          color="text.primary"
          sx={{
            fontSize: '1.1rem',
            ...(disablePlayerLink
              ? {}
              : {
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'underline'
                  }
                })
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!disablePlayerLink) onPlayerClick(player);
          }}
        >
          {player.nom}
        </Typography>

        {line2 && (
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '1rem' }}>
            {line2}
          </Typography>
        )}
        {line3 && (
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '1rem' }}>
            {line3}
          </Typography>
        )}
            {/* Badges section - same style as PlayerResults */}
            {(player.is_in_big_board || player.is_in_informe ||
              player.is_in_intel || player.is_in_fiche_technique || player.is_in_video || player.is_in_injury || player.is_in_tanteo || player.has_medidas) && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25, mt: 1 }}>
                {player.is_in_injury && (
                  <HealingIcon sx={{ color: 'red', fontSize: 20 }} />
                )}
                {player.is_in_tanteo && (
                  <TanteoBadge teamNom={player.tanteo_nom} compact />
                )}
                {player.is_in_big_board && (
                  <Tooltip title="Ranking">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#2196f3',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <StarIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
                {player.is_in_informe && (
                  <Tooltip title="Informe">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#4caf50',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <AssignmentIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
                {player.is_in_intel && (
                  <Tooltip title="INTEL">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#ff6f00',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <LightbulbIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
                {player.is_in_fiche_technique && (
                  <Tooltip title="Ficha Técnica">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#00bcd4',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <DescriptionIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
                {player.is_in_video && (
                  <Tooltip title="Video">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#9c27b0',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <VideoLibraryIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
                {player.has_medidas && (
                  <Tooltip title="Medidas">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#00bcd4',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <StraightenIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
                {player.has_documents && (
                  <Tooltip title="Documents">
                    <Box sx={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#795548',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      marginLeft: '-4px',
                      border: '1px solid #fff',
                    }}>
                      <FolderOpenIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}
      </Box>

      {!disablePlayerLink && player.url && (
        <ListItemSecondaryAction>
          <IconButton
            edge="end"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onPlayerUrlClick(player.url, e);
            }}
          >
            <Launch fontSize="small" />
          </IconButton>
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
};

/** =========================================================
 *  PLAYER CARD HORIZONTAL (compact, pour vue lignes)
 * ========================================================= */
const PlayerCardHorizontal = ({
  player,
  onPlayerClick,
  onPlayerUrlClick,
  mediaUrl,
  disablePlayerLink
}) => {
  const calculateDecimalAge = (dateNaissance, anneeNaissance) => {
    if (!dateNaissance || !anneeNaissance) return anneeNaissance;
    const now = new Date();
    const birthDate = new Date(dateNaissance);
    const ageInMilliseconds = now - birthDate;
    const ageInYears = ageInMilliseconds / (365.25 * 24 * 60 * 60 * 1000);
    return `${anneeNaissance} (${ageInYears.toFixed(1)})`;
  };

  const ageDisplay = calculateDecimalAge(player.date_naissance, player.annee_naissance);
  const subline = joinWithDash(
    player.poste,
    ageDisplay,
    player.taille ? `${player.taille}cm` : null
  );

  let teamLine = '';
  if (player.competitions_detail && player.competitions_detail.length > 0) {
    const equipesMap = new Map();
    player.competitions_detail.forEach((comp) => {
      if (!equipesMap.has(comp.equipe_id)) {
        equipesMap.set(comp.equipe_id, {
          equipe_nom: comp.equipe_nom,
          competitions: []
        });
      }
      equipesMap.get(comp.equipe_id).competitions.push(comp.competition_nom);
    });
    teamLine = Array.from(equipesMap.values())
      .map((e) => e.equipe_nom)
      .join(' / ');
  } else if (player.team) {
    teamLine = player.team;
  }

  const badges = [
    player.is_in_injury && { color: 'red', icon: <HealingIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Injury' },
    player.is_in_big_board && { color: '#2196f3', icon: <StarIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Ranking' },
    player.is_in_informe && { color: '#4caf50', icon: <AssignmentIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Informe' },
    player.is_in_intel && { color: '#ff6f00', icon: <LightbulbIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'INTEL' },
    player.is_in_fiche_technique && { color: '#00bcd4', icon: <DescriptionIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Ficha Técnica' },
    player.is_in_video && { color: '#9c27b0', icon: <VideoLibraryIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Video' },
    player.has_medidas && { color: '#00bcd4', icon: <StraightenIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Medidas' },
    player.has_documents && { color: '#795548', icon: <FolderOpenIcon sx={{ fontSize: 14, color: 'white' }} />, title: 'Documents' }
  ].filter(Boolean);

  return (
    <Paper
      id={`player-card-h-${player.id}`}
      elevation={1}
      onClick={!disablePlayerLink ? () => onPlayerClick(player) : undefined}
      sx={{
        flex: '0 0 auto',
        width: 168,
        p: 1.25,
        scrollSnapAlign: 'start',
        position: 'relative',
        cursor: disablePlayerLink ? 'default' : 'pointer',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': disablePlayerLink
          ? {}
          : {
              transform: 'translateY(-2px)',
              boxShadow: 4,
              borderColor: 'primary.light'
            }
      }}
    >
      {player.url && !disablePlayerLink && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onPlayerUrlClick(player.url, e);
          }}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 1,
            bgcolor: 'rgba(255,255,255,0.85)',
            '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
          }}
        >
          <Launch sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, position: 'relative' }}>
        <Avatar
          src={`${mediaUrl}/photos/${player.id}.png`}
          alt={player.nom}
          sx={{ width: 72, height: 72 }}
        />
        {player.forme_indicator && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -2,
              right: 'calc(50% - 38px)',
              backgroundColor: 'white',
              borderRadius: '50%',
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.25)'
            }}
          >
            {getFormIcon(player.forme_indicator)}
          </Box>
        )}
      </Box>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...(disablePlayerLink
            ? {}
            : { '&:hover': { color: 'primary.main' } })
        }}
        title={player.nom}
      >
        {player.nom}
      </Typography>

      {subline && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            display: 'block',
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={subline}
        >
          {subline}
        </Typography>
      )}

      {teamLine && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            display: 'block',
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontStyle: 'italic'
          }}
          title={teamLine}
        >
          {teamLine}
        </Typography>
      )}

      {(player.acb_seasons > 0 || player.is_in_tanteo) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {player.acb_seasons > 0 && (
            <AcbExperienceBadge seasons={player.acb_seasons} matches={player.acb_matches} compact />
          )}
          {player.is_in_tanteo && (
            <TanteoBadge teamNom={player.tanteo_nom} compact />
          )}
        </Box>
      )}

      {badges.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 0.25,
            mt: 0.75
          }}
        >
          {badges.map((b, i) => (
            <Tooltip key={i} title={b.title}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: b.color,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #fff'
                }}
              >
                {b.icon}
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}
    </Paper>
  );
};

/** =========================================================
 *  SEGUIMIENTO CONTENT HORIZONTAL (rail joueurs en lignes)
 * ========================================================= */
const SeguimientoContentHorizontal = ({
  seguimiento,
  onEdit,
  onDelete,
  onPlayerClick,
  onPlayerUrlClick,
  mediaUrl,
  disablePlayerLink
}) => {
  const sortedPlayers = [...seguimiento.players].sort((a, b) =>
    a.nom.localeCompare(b.nom)
  );

  return (
    <Paper
      elevation={1}
      sx={{
        mb: 2,
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <UserAvatar user={seguimiento.user} mediaUrl={mediaUrl} size={32} />
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0
          }}
          title={seguimiento.libelle}
        >
          {seguimiento.libelle}
        </Typography>
        <Chip
          size="small"
          label={sortedPlayers.length}
          sx={{
            fontWeight: 600,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            minWidth: 32
          }}
        />
        <IconButton size="small" onClick={() => onEdit(seguimiento.id)} color="primary">
          <Edit fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(seguimiento)} color="error">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          display: 'flex',
          alignItems: 'stretch',
          gap: 1.5,
          px: 2,
          py: 2,
          scrollSnapType: 'x proximity',
          '&::-webkit-scrollbar': { height: 8 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 4
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0,0,0,0.35)'
          },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' }
        }}
      >
        {sortedPlayers.length === 0 ? (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ p: 2, fontStyle: 'italic' }}
          >
            —
          </Typography>
        ) : (
          sortedPlayers.map((player) => (
            <PlayerCardHorizontal
              key={player.id}
              player={player}
              onPlayerClick={onPlayerClick}
              onPlayerUrlClick={onPlayerUrlClick}
              mediaUrl={mediaUrl}
              disablePlayerLink={disablePlayerLink}
            />
          ))
        )}
      </Box>
    </Paper>
  );
};

/** =========================================================
 *  Composant qui affiche 1 seguimiento dans un Paper
 * ========================================================= */
const SeguimientoContent = ({
  seguimiento,
  onEdit,
  onDelete,
  onPlayerClick,
  onPlayerUrlClick,
  mediaUrl,
  disablePlayerLink
}) => {
  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UserAvatar user={seguimiento.user} mediaUrl={mediaUrl} />
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            {seguimiento.libelle}
          </Typography>
        </Box>

        <Box>
          <IconButton
            size="small"
            onClick={() => onEdit(seguimiento.id)}
            color="primary"
            sx={{ mr: 1 }}
          >
            <Edit />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(seguimiento)} color="error">
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>

      <List disablePadding>
        {seguimiento.players
          .sort((a, b) => a.nom.localeCompare(b.nom))
          .map((player) => (
            <PlayerCard
              key={player.id}
              player={player}
              onPlayerClick={onPlayerClick}
              onPlayerUrlClick={onPlayerUrlClick}
              mediaUrl={mediaUrl}
              disablePlayerLink={disablePlayerLink}
            />
          ))}
      </List>
    </Paper>
  );
};

/** =========================================================
 *   ACCORDION PAR TAG 
 *   (on affiche les "seguimientos" sur 1, 2 ou 3 colonnes)
 * ========================================================= */
const TagAccordion = ({
  tag,
  seguimientos,
  onEdit,
  onDelete,
  onPlayerClick,
  onPlayerUrlClick,
  mediaUrl,
  expandedTags,
  setExpandedTags,
  updateSearchParams,
  disablePlayerLink,
  onGeneratePDFForTag,
  orientation = 'vertical',
  t
}) => {
  const isExpanded = expandedTags.has(tag.id);

  // On calcule le total de jugadores pour l'afficher à côté
  const totalPlayers = seguimientos.reduce(
    (acc, seg) => acc + seg.players.length,
    0
  );

  // On décide combien de colonnes en fonction du nombre de seguimientos
  const nbSeguimientos = seguimientos.length;
  let mdSize;
  if (nbSeguimientos === 1) mdSize = 12;
  else if (nbSeguimientos === 2) mdSize = 6;
  else mdSize = 4;

  const handleChange = () => {
    const newSet = new Set(expandedTags);
    if (newSet.has(tag.id)) {
      newSet.delete(tag.id);
    } else {
      newSet.add(tag.id);
    }
    setExpandedTags(newSet);
    // On met à jour l'URL, si vous conservez la logique des expanded dans searchParams
    updateSearchParams({ expandedTags: Array.from(newSet) });
  };

  const headerInfoEl = (
    <Box key="info" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Chip
        label={tag.libelle}
        style={{
          backgroundColor: tag.code_couleur,
          color: getContrastColor(tag.code_couleur),
          fontSize: '1.25rem',
          height: '48px',
          borderRadius: '8px'
        }}
      />
      <Typography color="text.secondary">
        {totalPlayers} {t('seguimientoTable.players')}
      </Typography>
    </Box>
  );

  const pdfButtonEl = (
    <Button
      key="pdf"
      variant="contained"
      startIcon={<PictureAsPdfIcon />}
      onClick={(e) => {
        e.stopPropagation();
        onGeneratePDFForTag(tag, seguimientos);
      }}
    >
      PDF
    </Button>
  );

  return (
    <Accordion
      id={`accordion-tag-${tag.id}`}
      expanded={isExpanded}
      onChange={handleChange}
      sx={{ mb: 1 }}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent:
              orientation === 'horizontal' ? 'flex-start' : 'space-between'
          }}
        >
          {headerInfoEl}
          {pdfButtonEl}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 2 }}>
        {orientation === 'horizontal' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {seguimientos.map((seg) => (
              <SeguimientoContentHorizontal
                key={seg.id}
                seguimiento={seg}
                onEdit={onEdit}
                onDelete={onDelete}
                onPlayerClick={onPlayerClick}
                onPlayerUrlClick={onPlayerUrlClick}
                mediaUrl={mediaUrl}
                disablePlayerLink={disablePlayerLink}
              />
            ))}
          </Box>
        ) : (
          <Grid container spacing={2}>
            {seguimientos.map((seg) => (
              <Grid item xs={12} md={mdSize} key={seg.id}>
                <SeguimientoContent
                  seguimiento={seg}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onPlayerClick={onPlayerClick}
                  onPlayerUrlClick={onPlayerUrlClick}
                  mediaUrl={mediaUrl}
                  disablePlayerLink={disablePlayerLink}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

/** =========================================================
 *                FILTER DRAWER (MOBILE)
 * ========================================================= */
const FilterDrawer = ({
  open,
  onClose,
  players,
  users,
  selectedPlayer,
  selectedUser,
  onPlayerChange,
  onUserChange,
  mediaUrl,
  tags,
  selectedTags,
  onTagsChange,
  userRole,
  seguimientoTypeFilter,
  setSeguimientoTypeFilter,
  t
}) => {
  const isSuperAdmin = userRole === 'super admin';

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      sx={{
        '& .MuiDrawer-paper': {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '80vh',
          pb: 2
        }
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">{t('seguimientoTable.filters')}</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {/* Cases à cocher Pro/Cantera (uniquement super admin) */}
          {isSuperAdmin && (
            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={seguimientoTypeFilter === 'pro'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSeguimientoTypeFilter('pro');
                          } else {
                            setSeguimientoTypeFilter('');
                          }
                        }}
                        name="pro"
                      />
                    }
                    label={t('seguimientoTable.pro')}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={seguimientoTypeFilter === 'cantera'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSeguimientoTypeFilter('cantera');
                          } else {
                            setSeguimientoTypeFilter('');
                          }
                        }}
                        name="cantera"
                      />
                    }
                    label={t('seguimientoTable.academy')}
                  />
                </FormGroup>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              options={players.players || []}
              getOptionLabel={(player) => player?.nom || ''}
              value={selectedPlayer}
              onChange={onPlayerChange}
              renderInput={(params) => (
                <TextField {...params} label={t('seguimientoTable.filterByPlayer')} />
              )}
              renderOption={(props, player) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Avatar
                    src={`${mediaUrl}/photos/${player.id}.png`}
                    alt={player.nom}
                    sx={{ width: 32, height: 32 }}
                  />
                  <Typography noWrap>{player.nom}</Typography>
                </Box>
              )}
              ListboxComponent={ListboxComponent}
              clearOnEscape
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              options={users}
              getOptionLabel={(user) =>
                user ? `${user.prenom} ${user.nom}` : ''
              }
              value={selectedUser}
              onChange={onUserChange}
              renderInput={(params) => (
                <TextField {...params} label={t('seguimientoTable.filterByUser')} />
              )}
              renderOption={(props, user) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <UserAvatar user={user} mediaUrl={mediaUrl} size={32} />
                  <Typography noWrap>{`${user.prenom} ${user.nom}`}</Typography>
                </Box>
              )}
              clearOnEscape
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              fullWidth
              options={tags}
              getOptionLabel={(tag) => tag.libelle}
              value={selectedTags}
              onChange={onTagsChange}
              renderInput={(params) => (
                <TextField {...params} label={t('seguimientoTable.filterByTags')} />
              )}
              renderOption={(props, tag) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Chip
                    size="small"
                    label={tag.libelle}
                    style={{
                      backgroundColor: tag.code_couleur,
                      color: getContrastColor(tag.code_couleur)
                    }}
                  />
                </Box>
              )}
              renderTags={(value, getTagProps) =>
                value.map((t, index) => (
                  <Chip
                    key={t.id}
                    size="small"
                    label={t.libelle}
                    {...getTagProps({ index })}
                    style={{
                      backgroundColor: t.code_couleur,
                      color: getContrastColor(t.code_couleur)
                    }}
                  />
                ))
              }
              clearOnEscape
            />
          </Grid>
        </Grid>
      </Box>
    </SwipeableDrawer>
  );
};

/** =========================================================
 *   addPageTemplate : Entête/Pied de page PDF
 * ========================================================= */
const addPageTemplate = (doc, clubName, departmentText) => {
  const pageNumber = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Récupérer les infos du club
  const finalClubName = clubName || localStorage.getItem('clubName') || 'Club';
  const clubTheme = JSON.parse(localStorage.getItem('clubTheme') || '{}');
  const primaryColor = clubTheme.primaryColor || '#EA7F00';

  // Convertir couleur hex en RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 234, g: 127, b: 0 };
  };
  const rgb = hexToRgb(primaryColor);

  doc.setDrawColor(primaryColor);
  doc.setLineWidth(2);
  doc.rect(10, 10, pageWidth - 20, pageHeight - 20);

  const bandHeight = 20;
  const bandY = pageHeight - 10 - bandHeight;
  doc.setFillColor(rgb.r, rgb.g, rgb.b);
  doc.rect(10, bandY, pageWidth - 20, bandHeight, 'F');

  const logoSize = 16;
  try {
    const logoY = bandY + (bandHeight - logoSize) / 2;

    // Déterminer les logos selon l'URL de l'application
    const currentUrl = window.location.href.toLowerCase();
    const isKansas = currentUrl.includes('kansas');
    const isMetropolitans = currentUrl.includes('metropolitans');
    const isByers = currentUrl.includes('byers');
    const logoLeft = isKansas ? `${process.env.PUBLIC_URL}/assets/kansas.png`
      : isMetropolitans ? `${process.env.PUBLIC_URL}/assets/metropolitans.png`
      : isByers ? `${process.env.PUBLIC_URL}/assets/byers.png`
      : `${process.env.PUBLIC_URL}/assets/LogoValenciaBasket.png`;
    const logoRight = isKansas ? `${process.env.PUBLIC_URL}/assets/kansas.png`
      : isMetropolitans ? `${process.env.PUBLIC_URL}/assets/metropolitans.png`
      : isByers ? `${process.env.PUBLIC_URL}/assets/byers.png`
      : `${process.env.PUBLIC_URL}/assets/alqueria-del-basket.png`;

    doc.addImage(logoLeft, 'PNG', 15, logoY, logoSize, logoSize);

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text(
      `${finalClubName} - ${departmentText || 'SCOUTING DEPARTMENT'}`,
      pageWidth / 2,
      bandY + bandHeight / 2 + 3,
      { align: 'center' }
    );

    doc.addImage(logoRight, 'PNG', pageWidth - 15 - logoSize, logoY, logoSize, logoSize);

    doc.setFontSize(8);
    doc.text(pageNumber.toString(), pageWidth / 2, bandY + bandHeight - 3, {
      align: 'center'
    });
  } catch (error) {
    console.error('Error adding images to PDF page:', error);
  }
  doc.setTextColor(0);
  addPdfDisclaimer(doc);
};

/** =========================================================
 *                SEGUIMIENTO TABLE
 * ========================================================= */
const SeguimientoTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const mediaUrl = getMediaBaseUrl();
  const section = process.env.REACT_APP_SECTION;

  // Infos utilisateur
  const userData = JSON.parse(localStorage.getItem('user'));
  const currentUserId = userData?.id || null;
  const userRole = (userData?.role || '').toLowerCase();

  // État local
  const [seguimientoTypeFilter, setSeguimientoTypeFilter] = useState('all');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [seguimientoToDelete, setSeguimientoToDelete] = useState(null);
  const [players, setPlayers] = useState([]);
  const [users, setUsers] = useState([]);
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);

  // Récupération depuis le context
  const {
    data,
    setData,
    loading,
    setLoading,
    error,
    setError,
    selectedPlayer,
    setSelectedPlayer,
    selectedUser,
    setSelectedUser,
    tags,
    setTags,
    expandedTags,
    setExpandedTags,
    scrollPosition,
    setScrollPosition,
    orientation,
    setOrientation
  } = useSeguimientoTable();
  const effectiveOrientation = isMobile ? 'vertical' : orientation;

  // Filtre "tags" sélectionnés
  const [selectedTags, setSelectedTags] = useState([]);

  // Variables pour restaurer la sélection depuis l'URL
  const [pendingPlayerId, setPendingPlayerId] = useState(null);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [pendingTagIds, setPendingTagIds] = useState([]);

  // Détecte le "POP" (retour nav)
  useOnPop(() => {
    // Si on a un scrollPosition mémorisé, on le restaure
    if (scrollPosition) {
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);
    }
  });

  // Gère le cas BFCache (Chrome/Safari)
  useEffect(() => {
    function handlePageShow(e) {
      if (e.persisted) {
        setTimeout(() => {
          window.scrollTo(0, scrollPosition);
        }, 0);
      }
    }
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [scrollPosition]);

  // =====================================================
  // CHARGEMENT DES DONNÉES
  // =====================================================
  const STALE_DURATION_MS = 2 * 60 * 1000; // 2 minutes
  const lastFetchTime = useRef(null);

  const refreshData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/seguimiento');
      setData(response.data);
      setError(null);
      lastFetchTime.current = Date.now();
    } catch (err) {
      console.error('Error al cargar los Seguimientos:', err);
      setError(t('seguimientoTable.loadError'));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshDataIfStale = useCallback(() => {
    const isStale =
      lastFetchTime.current === null ||
      Date.now() - lastFetchTime.current > STALE_DURATION_MS;
    if (isStale) refreshData();
  }, [refreshData]);

  // Lecture des paramètres URL au montage
  useEffect(() => {
    const expandedParam = searchParams.get('expanded');
    if (expandedParam) {
      const ids = expandedParam
        .split(',')
        .filter((id) => id.trim() !== '')
        .map((id) => parseInt(id, 10));
      // On met ça dans le set du context
      setExpandedTags(new Set(ids));
    }

    const playerId = searchParams.get('playerId');
    if (playerId) setPendingPlayerId(parseInt(playerId, 10));

    const userId = searchParams.get('userId');
    if (userId) setPendingUserId(parseInt(userId, 10));

    const tagIds = searchParams.get('tagIds');
    if (tagIds) {
      const splitted = tagIds.split(',').map((tid) => parseInt(tid, 10));
      setPendingTagIds(splitted);
    }
    // eslint-disable-next-line
  }, []);

  // Chargement initial
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [tagsResp, playersResp, usersResp] = await Promise.all([
          api.get('/api/tags'),
          api.get('/api/players/select'),
          api.get('/api/users')
        ]);

        setTags(tagsResp.data);
        setPlayers(playersResp.data);
        setUsers(Array.isArray(usersResp.data) ? usersResp.data : []);

        // Restauration du player
        if (pendingPlayerId && playersResp.data.players) {
          const foundP = playersResp.data.players.find(
            (p) => p.id === pendingPlayerId
          );
          if (foundP) setSelectedPlayer(foundP);
        }
        // Restauration du user
        if (pendingUserId && usersResp.data) {
          const foundU = usersResp.data.find((u) => u.id === pendingUserId);
          if (foundU) setSelectedUser(foundU);
        }
        // Restauration des tags
        if (pendingTagIds.length > 0 && tagsResp.data) {
          const foundTags = tagsResp.data.filter((t) =>
            pendingTagIds.includes(t.id)
          );
          if (foundTags.length > 0) setSelectedTags(foundTags);
        }
      } catch (err) {
        console.error('Error al cargar datos iniciales:', err);
      }
      // Ensuite on refresh la liste de seguimientos
      refreshData();
    };
    fetchInitialData();
    // eslint-disable-next-line
  }, []);

  // Re-charger sur le focus uniquement si les données sont périmées
  useEffect(() => {
    window.addEventListener('focus', refreshDataIfStale);
    return () => {
      window.removeEventListener('focus', refreshDataIfStale);
    };
  }, [refreshDataIfStale]);

  // =====================================================
  // HANDLERS CRUD
  // =====================================================
  const handleEditClick = (seguimientoId) => {
    navigate(`/seguimiento/edit/${seguimientoId}`);
  };

  const handleDelete = async (seguimientoId) => {
    try {
      await api.delete(`/api/seguimiento/${seguimientoId}`);
      await refreshData();
      setOpenDeleteDialog(false);
      setSeguimientoToDelete(null);
    } catch (err) {
      console.error('Error al eliminar el Seguimiento:', err);
      setError(t('seguimientoTable.deleteError'));
    }
  };

  // Mémoriser la position scroll avant d'aller sur la fiche joueur
  const handlePlayerClick = (player) => {
    setScrollPosition(window.scrollY);
    navigate(`/players/${player.id}`);
  };

  const handlePlayerUrlClick = (url, event) => {
    event.stopPropagation();
    window.open(url, '_blank');
  };

  // =====================================================
  // FILTRAGE
  // =====================================================
  const filteredData = useMemo(() => {
    if (!data) return [];

    return data.filter((seg) => {
      const segTypeLower = seg.type ? seg.type.toLowerCase() : '';

      let passTypeCheck = true;
      if (userRole === 'super admin') {
        if (seguimientoTypeFilter === 'pro') {
          passTypeCheck = segTypeLower === 'pro';
        } else if (seguimientoTypeFilter === 'cantera') {
          passTypeCheck = segTypeLower === 'cantera';
        }
      } else if (userRole === 'admin pro') {
        passTypeCheck = segTypeLower === 'pro';
      } else if (userRole === 'admin cantera') {
        passTypeCheck = segTypeLower === 'cantera';
      } else if (userRole === 'scout interne cantera' || userRole === 'scout externe cantera') {
        passTypeCheck = segTypeLower === 'cantera' && seg.user.id === currentUserId;
      } else if (userRole === 'entrenador' || userRole === 'pdp' || userRole === 'scout interne' || userRole === 'scout externe') {
        passTypeCheck = seg.user.id === currentUserId;
      }

      if (!passTypeCheck) return false;

      // Filtre joueur
      const matchesPlayer =
        !selectedPlayer ||
        seg.players.some((p) => p.id === selectedPlayer.id);
      if (!matchesPlayer) return false;

      // Filtre user
      const matchesUser = !selectedUser || seg.user.id === selectedUser.id;
      if (!matchesUser) return false;

      // Filtre tags
      const matchesTags =
        selectedTags.length === 0 ||
        seg.tags.some((t) => selectedTags.some((st) => st.id === t.id));

      return matchesTags;
    });
  }, [
    data,
    selectedPlayer,
    selectedUser,
    selectedTags,
    userRole,
    currentUserId,
    seguimientoTypeFilter
  ]);

  // Regrouper par tag
  const groupedByTag = useMemo(() => {
    if (!tags) return [];
    const map = new Map();
    tags.forEach((t) => {
      map.set(t.id, { tag: t, seguimientos: [] });
    });

    filteredData.forEach((seguimiento) => {
      seguimiento.tags.forEach((t) => {
        if (map.has(t.id)) {
          map.get(t.id).seguimientos.push(seguimiento);
        }
      });
    });
    // Ne garder que les tags qui ont au moins 1 seguimiento
    return Array.from(map.values()).filter((g) => g.seguimientos.length > 0);
  }, [tags, filteredData]);

  // =====================================================
  // MISE À JOUR DES URL PARAMS
  // =====================================================
  const updateSearchParams = (newState) => {
    const params = new URLSearchParams(searchParams.toString());

    // expanded
    if (newState.expandedTags) {
      if (newState.expandedTags.length > 0) {
        params.set('expanded', newState.expandedTags.join(','));
      } else {
        params.delete('expanded');
      }
    } else if (expandedTags.size > 0) {
      params.set('expanded', Array.from(expandedTags).join(','));
    } else {
      params.delete('expanded');
    }

    // selectedPlayer
    if (newState.selectedPlayer !== undefined) {
      const val = newState.selectedPlayer ? newState.selectedPlayer.id : '';
      val ? params.set('playerId', val) : params.delete('playerId');
    } else if (selectedPlayer) {
      params.set('playerId', selectedPlayer.id);
    } else {
      params.delete('playerId');
    }

    // selectedUser
    if (newState.selectedUser !== undefined) {
      const val = newState.selectedUser ? newState.selectedUser.id : '';
      val ? params.set('userId', val) : params.delete('userId');
    } else if (selectedUser) {
      params.set('userId', selectedUser.id);
    } else {
      params.delete('userId');
    }

    // selectedTags
    if (newState.selectedTags !== undefined) {
      const tIds = newState.selectedTags.map((tg) => tg.id);
      if (tIds.length > 0) {
        params.set('tagIds', tIds.join(','));
      } else {
        params.delete('tagIds');
      }
    } else if (selectedTags.length > 0) {
      params.set(
        'tagIds',
        selectedTags.map((tg) => tg.id).join(',')
      );
    } else {
      params.delete('tagIds');
    }

    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    // On met à jour l'URL dès que la sélection ou l'expansion change
    updateSearchParams({});
    // eslint-disable-next-line
  }, [selectedPlayer, selectedUser, selectedTags, expandedTags]);

  // Auto-scroll vers l'accordéon ouvert quand vient de PlayerProfile (une seule fois)
  const hasAutoScrolled = useRef(false);
  useEffect(() => {
    if (hasAutoScrolled.current) return;
    const shouldScroll = searchParams.get('expanded') && expandedTags.size > 0 && !loading && data && data.length > 0;

    if (shouldScroll) {
      const scrollToAccordion = () => {
        const firstExpandedId = Array.from(expandedTags)[0];
        const accordionElement = document.getElementById(`accordion-tag-${firstExpandedId}`);

        if (accordionElement) {
          const elementPosition = accordionElement.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementPosition - 100,
            behavior: 'smooth'
          });
          hasAutoScrolled.current = true;
        }
      };

      const timeouts = [100, 500, 1000];
      const timeoutIds = timeouts.map(delay =>
        setTimeout(scrollToAccordion, delay)
      );

      return () => {
        timeoutIds.forEach(id => clearTimeout(id));
      };
    }
  }, [expandedTags, loading, data, searchParams]);

  // =====================================================
  // GÉNÉRATION PDF
  // =====================================================
  const generatePDFForTag = async (tag, seguimientos) => {
    try {
      // Récupérer les infos du club
      const clubName = localStorage.getItem('clubName') || 'Club';
      const departmentText = t('pdf.footer.department');
      const clubTheme = JSON.parse(localStorage.getItem('clubTheme') || '{}');
      const primaryColor = clubTheme.primaryColor || '#EA7F00';

      // Convertir couleur hex en RGB pour le tableau
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? [
          parseInt(result[1], 16),
          parseInt(result[2], 16),
          parseInt(result[3], 16)
        ] : [234, 127, 0];
      };
      const headerColor = hexToRgb(primaryColor);

      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const dateStr = `${day}-${month}-${year}`;

      const doc = new jsPDF();
      addPageTemplate(doc, clubName, departmentText);

      let yPos = 20;
      doc.setFontSize(18);
      doc.setTextColor(0);
      doc.text(
        `${clubName} ${t('seguimientoTable.title')} - ${tag.libelle} - ${dateStr}`,
        doc.internal.pageSize.width / 2,
        yPos,
        { align: 'center' }
      );
      yPos += 15;

      for (let sIndex = 0; sIndex < seguimientos.length; sIndex++) {
        const seguimiento = seguimientos[sIndex];

        if (yPos > doc.internal.pageSize.height - 50) {
          doc.addPage();
          addPageTemplate(doc, clubName, departmentText);
          yPos = 20;
        }

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(seguimiento.libelle, 20, yPos);
        yPos += 8;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(0);

        const playersSorted = seguimiento.players.sort((a, b) =>
          a.nom.localeCompare(b.nom)
        );

        const rowDataArray = playersSorted.map((p) => {
          const parts = [];
          if (p.poste) parts.push(p.poste);
          if (p.annee_naissance) {
            // Calculate decimal age if date_naissance exists
            if (p.date_naissance) {
              const now = new Date();
              const birthDate = new Date(p.date_naissance);
              const ageInMilliseconds = now - birthDate;
              const ageInYears = ageInMilliseconds / (365.25 * 24 * 60 * 60 * 1000);
              parts.push(`${p.annee_naissance} (${ageInYears.toFixed(1)})`);
            } else {
              parts.push(p.annee_naissance);
            }
          }
          if (p.taille) parts.push(`${p.taille}cm`);

          if (p.nationalite) {
            const natPassport = p.passport
              ? `${p.nationalite} - ${p.passport}`
              : p.nationalite;
            parts.push(natPassport);
          }

          // Add competitions_detail if available, otherwise fallback to old fields
          if (p.competitions_detail && p.competitions_detail.length > 0) {
            // Group competitions by team
            const equipesMap = new Map();
            p.competitions_detail.forEach(comp => {
              if (!equipesMap.has(comp.equipe_id)) {
                equipesMap.set(comp.equipe_id, {
                  equipe_nom: comp.equipe_nom,
                  competitions: []
                });
              }
              equipesMap.get(comp.equipe_id).competitions.push(comp.competition_nom);
            });

            // Format as: Team1 (Comp1, Comp2)
            const teamsStr = Array.from(equipesMap.values())
              .map(equipe => `${equipe.equipe_nom} (${equipe.competitions.join(', ')})`)
              .join(' - ');

            if (teamsStr) parts.push(teamsStr);
          } else {
            // Fallback to old fields
            if (p.team) parts.push(p.team);
            const leagues = [p.league_1, p.league_2, p.competicion_europa].filter(
              Boolean
            );
            if (leagues.length > 0) {
              parts.push(leagues.join(' - '));
            }
          }

          const infoLine = parts.join(' - ');
          return [p.nom, infoLine];
        });

        doc.autoTable({
          startY: yPos,
          head: [['Nombre', 'Infos']],
          body: rowDataArray,
          theme: 'grid',
          headStyles: {
            fillColor: headerColor,
            textColor: [255, 255, 255],
            fontSize: 9,
            fontStyle: 'bold'
          },
          styles: {
            fontSize: 8,
            cellPadding: 2,
            overflow: 'linebreak'
          },
          margin: { left: 15, right: 15, bottom: 30 },
          didDrawPage: () => {
            addPageTemplate(doc, clubName, departmentText);
          }
        });

        yPos = doc.lastAutoTable.finalY + 10;
      }

      doc.save(`${clubName} ${t('seguimientoTable.title')} - ${tag.libelle} - ${dateStr}.pdf`);
    } catch (err) {
      console.error('Erreur génération PDF:', err);
    }
  };

  // =====================================================
  // RENDER
  // =====================================================
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }
  if (!data || data.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {t('seguimientoTable.noSeguimientos')}
      </Alert>
    );
  }

  // Pour les rôles "entrenador" / "pdp", désactiver lien vers la fiche joueur
  const disablePlayerLink = userRole === 'entrenador' || userRole === 'pdp';

  return (
    <Paper elevation={2}>
      <Box sx={{ p: 2, pb: isMobile ? 7 : 2 }}>
        {/* Filtros (Desktop) */}
        {!isMobile && (
          <>
            {userRole === 'super admin' && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <FormControl component="fieldset" fullWidth>
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={seguimientoTypeFilter === 'pro'}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSeguimientoTypeFilter('pro');
                              } else {
                                setSeguimientoTypeFilter('');
                              }
                            }}
                            name="pro"
                          />
                        }
                        label={t('seguimientoTable.pro')}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={seguimientoTypeFilter === 'cantera'}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSeguimientoTypeFilter('cantera');
                              } else {
                                setSeguimientoTypeFilter('');
                              }
                            }}
                            name="cantera"
                          />
                        }
                        label={t('seguimientoTable.academy')}
                      />
                    </FormGroup>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  fullWidth
                  options={players.players || []}
                  getOptionLabel={(player) => player?.nom || ''}
                  value={selectedPlayer}
                  onChange={(_, newValue) => {
                    setSelectedPlayer(newValue);
                    updateSearchParams({ selectedPlayer: newValue });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label={t('seguimientoTable.filterByPlayer')} />
                  )}
                  renderOption={(props, p) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Avatar
                        src={`${mediaUrl}/photos/${p.id}.png`}
                        alt={p.nom}
                        sx={{ width: 32, height: 32 }}
                      />
                      <Typography noWrap>{p.nom}</Typography>
                    </Box>
                  )}
                  ListboxComponent={ListboxComponent}
                  clearOnEscape
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Autocomplete
                  fullWidth
                  options={users}
                  getOptionLabel={(u) => (u ? `${u.prenom} ${u.nom}` : '')}
                  value={selectedUser}
                  onChange={(_, newValue) => {
                    setSelectedUser(newValue);
                    updateSearchParams({ selectedUser: newValue });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label={t('seguimientoTable.filterByUser')} />
                  )}
                  renderOption={(props, u) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <UserAvatar user={u} mediaUrl={mediaUrl} size={32} />
                      <Typography noWrap>{`${u.prenom} ${u.nom}`}</Typography>
                    </Box>
                  )}
                  clearOnEscape
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Autocomplete
                  multiple
                  fullWidth
                  options={tags}
                  getOptionLabel={(t) => t.libelle}
                  value={selectedTags}
                  onChange={(_, newValue) => {
                    setSelectedTags(newValue);
                    updateSearchParams({ selectedTags: newValue });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label={t('seguimientoTable.filterByTags')} />
                  )}
                  renderOption={(props, t) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Chip
                        size="small"
                        label={t.libelle}
                        style={{
                          backgroundColor: t.code_couleur,
                          color: getContrastColor(t.code_couleur)
                        }}
                      />
                    </Box>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((tg, index) => (
                      <Chip
                        key={tg.id}
                        size="small"
                        label={tg.libelle}
                        {...getTagProps({ index })}
                        style={{
                          backgroundColor: tg.code_couleur,
                          color: getContrastColor(tg.code_couleur)
                        }}
                      />
                    ))
                  }
                  clearOnEscape
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* Petite bannière "x tags trouvés" */}
        <Box
          sx={{
            mx: 2,
            mt: 2,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            p: 2,
            bgcolor: theme.palette.primary.main,
            color: 'white',
            borderRadius: 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              fontWeight: 'bold',
              fontSize: '1.1rem'
            }}
          >
            {groupedByTag.length}
          </Box>
          <Typography variant="subtitle1">{t('seguimientoTable.seguimientosFound')}</Typography>
          {!isMobile && (
            <Tooltip title={t('seguimientoTable.viewToggleTooltip')}>
              <ToggleButtonGroup
                value={orientation}
                exclusive
                size="small"
                onChange={(_, value) => {
                  if (value) setOrientation(value);
                }}
                sx={{
                  ml: 'auto',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  borderRadius: 1,
                  '& .MuiToggleButton-root': {
                    color: 'white',
                    border: 'none',
                    px: 1.25,
                    py: 0.5,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(255,255,255,0.85)',
                      color: theme.palette.primary.main,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' }
                    },
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                  }
                }}
              >
                <ToggleButton value="vertical" aria-label={t('seguimientoTable.viewVertical')}>
                  <Tooltip title={t('seguimientoTable.viewVertical')}>
                    <ViewColumnIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="horizontal" aria-label={t('seguimientoTable.viewHorizontal')}>
                  <Tooltip title={t('seguimientoTable.viewHorizontal')}>
                    <ViewStreamIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
          )}
        </Box>

        {/* Liste des TagAccordion */}
        {groupedByTag.map(({ tag, seguimientos }) => (
          <TagAccordion
            key={tag.id}
            tag={tag}
            seguimientos={seguimientos}
            onEdit={handleEditClick}
            onDelete={(s) => {
              setSeguimientoToDelete(s);
              setOpenDeleteDialog(true);
            }}
            onPlayerClick={handlePlayerClick}
            onPlayerUrlClick={handlePlayerUrlClick}
            mediaUrl={mediaUrl}
            expandedTags={expandedTags}
            setExpandedTags={setExpandedTags}
            updateSearchParams={updateSearchParams}
            disablePlayerLink={disablePlayerLink}
            onGeneratePDFForTag={generatePDFForTag}
            orientation={effectiveOrientation}
            t={t}
          />
        ))}

        {/* Bouton flottant (mobile) */}
        {isMobile && (
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: theme.zIndex.fab
            }}
            onClick={() => setOpenFilterDrawer(true)}
          >
            <FilterList />
          </Fab>
        )}
      </Box>

      {/* Filter Drawer (mobile) */}
      <FilterDrawer
        open={openFilterDrawer}
        onClose={() => setOpenFilterDrawer(false)}
        players={players}
        users={users}
        selectedPlayer={selectedPlayer}
        selectedUser={selectedUser}
        onPlayerChange={(_, newValue) => {
          setSelectedPlayer(newValue);
          updateSearchParams({ selectedPlayer: newValue });
        }}
        onUserChange={(_, newValue) => {
          setSelectedUser(newValue);
          updateSearchParams({ selectedUser: newValue });
        }}
        mediaUrl={mediaUrl}
        tags={tags}
        selectedTags={selectedTags}
        onTagsChange={(_, newValue) => {
          setSelectedTags(newValue);
          updateSearchParams({ selectedTags: newValue });
        }}
        userRole={userRole}
        seguimientoTypeFilter={seguimientoTypeFilter}
        setSeguimientoTypeFilter={setSeguimientoTypeFilter}
        t={t}
      />

      {/* Dialog confirmation de suppression */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setSeguimientoToDelete(null);
        }}
      >
        <DialogTitle>{t('seguimientoTable.confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('seguimientoTable.confirmDeleteMessage', { name: seguimientoToDelete?.libelle })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeleteDialog(false);
              setSeguimientoToDelete(null);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            color="error"
            onClick={() => handleDelete(seguimientoToDelete?.id)}
            variant="contained"
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SeguimientoTable;